// Ported from vendor-ref/mara/app/ui/src/utils/confirmMenuItem.js: a Menu item that needs a second click
// ("Confirm delete") within 3 s, keeping the menu open between the two clicks.
import { computed, nextTick, ref, unref, type MaybeRef, type Ref } from 'vue';
import type { MenuItem } from 'primevue/menuitem';

interface PopupMenu { hide: () => void; list?: HTMLElement; container?: HTMLElement; focused?: boolean; focusedOptionIndex?: string }

export function useConfirmMenuItem(options: {
  menu: Ref<PopupMenu | null | undefined>;
  onConfirm: () => void;
  label?: MaybeRef<string>;
  icon?: MaybeRef<string>;
  confirmIcon?: string;
  timeout?: number;
}) {
  const { menu, onConfirm, label = 'Delete', icon = 'i-tabler-trash', confirmIcon = 'i-tabler-trash-filled', timeout = 3000 } = options;
  const pending = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  function handleClick() {
    const m = menu.value;
    if (!pending.value) {
      pending.value = true;
      clearTimeout(timer);
      timer = setTimeout(() => { pending.value = false; }, timeout);
      if (!m) return;
      // Prevent the menu from hiding on this click
      const origHide = m.hide;
      m.hide = () => {};
      nextTick(() => {
        m.hide = origHide;
        const { list, container } = m;
        if (list && container) {
          const items = container.querySelectorAll('li[data-pc-section="item"][data-p-disabled="false"]');
          const last = items[items.length - 1];
          if (last) { m.focused = true; m.focusedOptionIndex = last.id; list.focus(); }
        }
      });
      return;
    }
    pending.value = false;
    clearTimeout(timer);
    onConfirm();
  }

  const menuItem = computed<MenuItem>(() => ({
    label: pending.value ? `Confirm ${unref(label).toLowerCase()}` : unref(label),
    icon: pending.value ? confirmIcon : unref(icon),
    class: 'text-red',
    command: handleClick,
  }));

  return { menuItem, pending };
}
